import React from 'react';
import { Button } from '../../components/ui/button';
import { formatDate } from '../../lib/utils';
import SocialShareButtons from '../../components/SocialShareButtons';
import {
  CheckCircle2,
  Bookmark,
  BookmarkCheck,
  Share2,
  Zap,
  ExternalLink,
} from 'lucide-react';

export const JobDetailSidebar = ({
  job,
  hasApplied,
  handleApplyClick,
  handleSave,
  isSaved,
  handleShare,
  isAuthenticated,
}) => {
  const isExternal = job.is_external || job.application_type === 'external';
  return (
    <div className="lg:col-span-1">
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 sticky top-24">
        {/* Application type label */}
        {isExternal ? (
          <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700" data-testid="apply-type-external">
            <ExternalLink className="w-3.5 h-3.5" />
            External Application
          </div>
        ) : (
          <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700" data-testid="apply-type-easy">
            <Zap className="w-3.5 h-3.5" />
            Easy Apply on INASW
          </div>
        )}

        {/* Apply button */}
        {isExternal ? (
          <>
            <Button
              onClick={handleApplyClick}
              className="w-full min-h-12 h-auto py-3 text-sm mb-2 bg-blue-600 hover:bg-blue-700 text-white whitespace-normal leading-tight"
              data-testid="apply-btn"
            >
              <ExternalLink className="w-4 h-4 mr-2 flex-shrink-0" />
              {isAuthenticated ? 'Continue to Employer Application' : 'Sign In to Continue to Employer Application'}
            </Button>
            <p className="text-xs text-gray-500 mb-4 leading-relaxed">
              You'll complete this application on the employer's official website.
              {hasApplied && ' You started this application.'}
            </p>
          </>
        ) : hasApplied ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-medium">Applied</span>
            </div>
            <p className="text-sm text-emerald-600 mt-1">
              You've already applied to this position
            </p>
          </div>
        ) : (
          <Button
            onClick={handleApplyClick}
            className="w-full h-12 text-base mb-4 bg-green-600 hover:bg-green-700 text-white"
            data-testid="apply-btn"
          >
            <Zap className="w-4 h-4 mr-2" />
            SW Express Apply™
          </Button>
        )}

        {/* Save & Share */}
        <div className="flex gap-2 mb-4">
          <Button
            variant="outline"
            onClick={handleSave}
            className={`flex-1 ${isSaved ? 'text-secondary border-secondary' : ''}`}
            data-testid="save-btn"
          >
            {isSaved ? (
              <BookmarkCheck className="w-4 h-4 mr-2 fill-current" />
            ) : (
              <Bookmark className="w-4 h-4 mr-2" />
            )}
            {isSaved ? 'Saved' : 'Save'}
          </Button>
          <Button variant="outline" onClick={handleShare} data-testid="share-btn">
            <Share2 className="w-4 h-4" />
          </Button>
        </div>

        {/* Social Share Buttons */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-700 mb-3">Share this job:</p>
          <SocialShareButtons job={job} variant="compact" />
        </div>

        {/* Job Info */}
        <div className="border-t pt-6">
          <h3 className="font-semibold text-gray-900 mb-4">Job Summary</h3>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Posted</dt>
              <dd className="text-gray-900">{formatDate(job.created_at)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Job Type</dt>
              <dd className="text-gray-900">{job.job_type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">License</dt>
              <dd className="text-gray-900">{job.license_required}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Location</dt>
              <dd className="text-gray-900">{job.location}</dd>
            </div>
            {job.is_remote && (
              <div className="flex justify-between">
                <dt className="text-gray-500">Remote</dt>
                <dd className="text-gray-900">Yes</dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </div>
  );
};
